import { UISimulation } from '@/components/help/UISimulation';
import { SimShell } from './simShell';

const D = 1900;

// AI-generated prompts — 4 frames: wizard step 6 -> loading -> 4 results ->
// accept.
function APStep(): JSX.Element {
  return (
    <SimShell step={1} total={4} title="Wizard step 6">
      <div className="text-xs text-dash-secondary">
        Generate Prompts fires four generators in parallel from your site context.
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
        {['Writer', 'Reviewer', 'Brainstorm', 'Illustrator'].map((p) => (
          <span
            key={p}
            className="rounded border border-dash-border bg-dash-surface text-dash-secondary px-2 py-0.5"
          >
            {p}
          </span>
        ))}
      </div>
    </SimShell>
  );
}

function APLoading(): JSX.Element {
  return (
    <SimShell step={2} total={4} title="Generating">
      <div className="text-[10px] font-mono uppercase text-warning">
        four generators running…
      </div>
      <div className="mt-2 space-y-1.5">
        {[60, 80, 45, 70].map((w, i) => (
          <div key={i} className="h-2 rounded bg-dash-surface overflow-hidden">
            <div className="h-full bg-warning/60" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
    </SimShell>
  );
}

function APResults(): JSX.Element {
  return (
    <SimShell step={3} total={4} title="Per-persona cards">
      <ul className="space-y-1 text-[11px]">
        {[
          ['Writer', '~1.8k tokens', 'text-success'],
          ['Reviewer', '~1.5k tokens', 'text-success'],
          ['Brainstorm', '~1.1k tokens', 'text-success'],
          ['Illustrator', '~0.9k tokens', 'text-warning'],
        ].map(([p, tok, cls]) => (
          <li
            key={p}
            className="flex items-center justify-between rounded border border-dash-border bg-dash-surface px-3 py-1"
          >
            <span className="text-dash-primary">{p}</span>
            <span className={'font-mono ' + cls}>{tok} ✓</span>
          </li>
        ))}
      </ul>
    </SimShell>
  );
}

function APAccept(): JSX.Element {
  return (
    <SimShell step={4} total={4} title="Accept">
      <div className="rounded border border-success/40 bg-success/10 px-3 py-2 text-xs">
        <div className="text-dash-primary">Generated bodies seeded into the new site.</div>
        <div className="text-[10px] font-mono uppercase text-success mt-0.5">
          auto-test passed · sparkles in lineage
        </div>
      </div>
    </SimShell>
  );
}

export function AIPromptGenerationSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <APStep />, label: 'Step 6 fires the generators', duration: D },
        { frame: <APLoading />, label: 'Four run in parallel', duration: D },
        { frame: <APResults />, label: 'Token counts and auto-test', duration: D },
        { frame: <APAccept />, label: 'Accept seeds the site', duration: D },
      ]}
    />
  );
}

// Testing your prompts — 3 frames: test panel -> run -> output.
function PTPanel(): JSX.Element {
  return (
    <SimShell step={1} total={3} title="Test panel">
      <div className="rounded border border-dash-border bg-dash-surface p-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-dash-muted">Show user message</div>
        <pre className="mt-1 max-h-14 overflow-hidden rounded bg-dash-bg p-2 text-[10px] text-dash-secondary">
          {'{ "topic": "protein timing", "intent": "how-to" }'}
        </pre>
      </div>
    </SimShell>
  );
}

function PTRun(): JSX.Element {
  return (
    <SimShell step={2} total={3} title="Run">
      <div className="rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs">
        <span className="text-accent font-mono text-[10px] uppercase">running test…</span>
        <div className="text-dash-secondary mt-1">
          The assembled system prompt is sent with this sample input.
        </div>
      </div>
    </SimShell>
  );
}

function PTOutput(): JSX.Element {
  return (
    <SimShell step={3} total={3} title="Output">
      <pre className="max-h-20 overflow-hidden rounded border border-dash-border bg-dash-bg p-2 text-[10px] text-dash-secondary">
        {'[\n  "When to take protein after a long run",\n  "Carbs vs protein in recovery",\n  ...\n]'}
      </pre>
    </SimShell>
  );
}

export function PromptTestingSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <PTPanel />, label: 'Preview the user message', duration: D },
        { frame: <PTRun />, label: 'Run against sample input', duration: D },
        { frame: <PTOutput />, label: 'Inspect the raw output', duration: D },
      ]}
    />
  );
}

// Syncing from templates — 4 frames: diverged badge -> sync -> diff -> accept.
function PSDiverged(): JSX.Element {
  return (
    <SimShell step={1} total={4} title="Diverged from template">
      <div className="flex items-center justify-between rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
        <span className="text-dash-primary">Writer (this site)</span>
        <span className="font-mono text-[10px] uppercase text-warning">behind template</span>
      </div>
    </SimShell>
  );
}

function PSSync(): JSX.Element {
  return (
    <SimShell step={2} total={4} title="Sync from template">
      <div className="rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-dash-secondary">
        Pull the latest template body into this site&apos;s persona.
        <div className="mt-1.5 text-accent font-mono text-[10px] uppercase">[ Sync from template ]</div>
      </div>
    </SimShell>
  );
}

function PSDiff(): JSX.Element {
  return (
    <SimShell step={3} total={4} title="Diff">
      <pre className="rounded border border-dash-border bg-dash-bg p-2 text-[10px] leading-5">
        <span className="text-error">- never make absolutist claims.</span>
        {'\n'}
        <span className="text-success">+ never make absolutist claims; cite a study.</span>
      </pre>
    </SimShell>
  );
}

function PSAccept(): JSX.Element {
  return (
    <SimShell step={4} total={4} title="Accept">
      <div className="rounded border border-success/40 bg-success/10 px-3 py-2 text-xs">
        <div className="text-dash-primary">New site version created and activated.</div>
        <div className="text-[10px] font-mono uppercase text-success mt-0.5">
          template edits never auto-propagate
        </div>
      </div>
    </SimShell>
  );
}

export function PromptSyncingSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <PSDiverged />, label: 'A drift badge appears', duration: D },
        { frame: <PSSync />, label: 'Click Sync from template', duration: D },
        { frame: <PSDiff />, label: 'Review the diff', duration: D },
        { frame: <PSAccept />, label: 'Accept the synced version', duration: D },
      ]}
    />
  );
}
